import IMask from 'imask';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { i18TranslationNS, Translation } from '@shared/config/i18n';

const CURRENT_YEAR = new Date().getFullYear();

export const dateToIso = (date) => {
	const y = date.getFullYear();
	const m = String(date.getMonth() + 1).padStart(2, '0');
	const d = String(date.getDate()).padStart(2, '0');
	return `${y}-${m}-${d}`;
};

export const isoToDisplay = (iso, sep) => {
	if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return '';
	const [y, m, d] = iso.split('-');
	return `${d}${sep}${m}${sep}${y}`;
};

// Single source of truth for the disablePast rule — shared by ReminderCalendar's
// cell-disabling and DateInput's typed-input completion, so both entry points
// reject the same dates.
export const isDateBeforeToday = (date) => {
	const today = new Date();
	today.setHours(0, 0, 0, 0);
	const compare = new Date(date);
	compare.setHours(0, 0, 0, 0);
	return compare < today;
};

export const useDateMaskOptions = ({ minYear = 1900, maxYear = CURRENT_YEAR } = {}) => {
	const { t } = useTranslation(i18TranslationNS);
	const sep = t(Translation.DATE_INPUT_SEPARATOR);

	return useMemo(
		() => ({
			mask: Date,
			pattern: `d{${sep}}m{${sep}}Y`,
			blocks: {
				d: { mask: IMask.MaskedRange, from: 1, to: 31, maxLength: 2, autofix: 'pad' },
				m: { mask: IMask.MaskedRange, from: 1, to: 12, maxLength: 2, autofix: 'pad' },
				Y: { mask: IMask.MaskedRange, from: minYear, to: maxYear },
			},
			format: (date) => isoToDisplay(dateToIso(date), sep),
			parse: (str) => {
				const [d, m, y] = str.split(sep).map(Number);
				return new Date(y, m - 1, d);
			},
			lazy: true,
		}),
		[sep, minYear, maxYear],
	);
};
